import { useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import type { PsePaymentInput } from "@/lib/pse-payment";

export interface DirectPaymentResult {
  success: boolean;
  payment_id?: number;
  status?: string;
  status_detail?: string;
  error?: string;
  pending?: boolean;
  redirect_url?: string;
}

interface DirectPaymentParams {
  organization_id: string;
  token?: string;
  payment_method_id: string;
  amount: number;
  installments?: number;
  payer_email?: string;
  description?: string;
  plan_id?: string;
  mode?: string;
  payment_intent_id: string;
  extra_pack_id?: string;
  pse?: PsePaymentInput;
  plan_upgrade_request_id?: string;
}

export function useDirectPayment() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DirectPaymentResult | null>(null);

  const pay = useCallback(async (params: DirectPaymentParams): Promise<DirectPaymentResult> => {
    setLoading(true);
    setResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("mercadopago-direct-payment", {
        body: params,
      });

      if (error) {
        let message = "Error al procesar el pago";
        if ("context" in error && error.context instanceof Response) {
          try {
            const body = await error.context.json();
            message = body?.error || message;
          } catch { /* non-JSON response */ }
        }
        throw new Error(message);
      }

      const res = data as DirectPaymentResult;
      if (!res?.success) {
        throw new Error(res?.error || "El pago no pudo ser procesado");
      }
      setResult(res);
      return res;
    } catch (err: unknown) {
      const errorResult: DirectPaymentResult = {
        success: false,
        error: err instanceof Error ? err.message : "Error al procesar el pago",
      };
      setResult(errorResult);
      return errorResult;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setLoading(false);
  }, []);

  return { pay, loading, result, reset };
}
